import { createMiddleware } from "@tanstack/react-start";
import { sanitizeForSentry } from "./sentry-config";

type SentryModule = typeof import("@sentry/tanstackstart-react");
type LoggerModule = typeof import("@/lib/logger");

let serverDepsPromise: Promise<{
  Sentry: SentryModule;
  getLogger: LoggerModule["getLogger"];
}> | null = null;

async function getServerDeps() {
  serverDepsPromise ??= Promise.all([
    import("@sentry/tanstackstart-react"),
    import("@/lib/logger"),
  ]).then(([Sentry, loggerModule]) => ({
    Sentry,
    getLogger: loggerModule.getLogger,
  }));

  return await serverDepsPromise;
}

const MAX_BODY_SIZE = 64 * 1024;
const MAX_ERROR_DETAIL_LENGTH = 512;

function isRedirectResponse(response: Response): boolean {
  return response.status >= 300 && response.status < 400;
}

function formatResponseMessage(response: Response): string {
  const statusText = response.statusText?.trim();
  return statusText ? `Response ${response.status} ${statusText}` : `Response ${response.status}`;
}

function toSafeDetail(value: unknown): string | undefined {
  if (value == null) return undefined;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function truncateDetail(detail: string): string {
  if (detail.length <= MAX_ERROR_DETAIL_LENGTH) return detail;
  return `${detail.slice(0, MAX_ERROR_DETAIL_LENGTH)}...[truncated]`;
}

function buildResponseErrorMessage(response: Response, detail?: string): string {
  const base = formatResponseMessage(response);
  if (!detail) return base;
  return `${base}: ${truncateDetail(detail)}`;
}

function safeRequestUrl(url: URL): string {
  return `${url.origin}${url.pathname}`;
}

async function readResponseBody(response: Response): Promise<unknown> {
  try {
    const cloned = response.clone();
    const parsedBody = await tryReadBody(cloned.body, cloned.headers.get("content-type"));
    if (parsedBody !== undefined) {
      return parsedBody;
    }
  } catch {}

  try {
    const fallbackText = await response.clone().text();
    return fallbackText.trim() ? fallbackText : undefined;
  } catch {
    return undefined;
  }
}

async function tryReadBody(
  body: ReadableStream<Uint8Array> | null,
  contentType: string | null,
): Promise<unknown> {
  if (!body) return undefined;
  if (!contentType?.includes("application/json") && !contentType?.includes("text/"))
    return undefined;

  try {
    const reader = body.getReader();
    const chunks: Uint8Array[] = [];
    let totalSize = 0;

    while (true) {
      const result = await reader.read();
      if (result.done) break;

      totalSize += result.value.byteLength;
      if (totalSize > MAX_BODY_SIZE) {
        reader.cancel();
        return `[body too large: >${MAX_BODY_SIZE} bytes]`;
      }
      chunks.push(result.value);
    }

    const merged = new Uint8Array(chunks.reduce((len, c) => len + c.length, 0));
    let offset = 0;
    for (const chunk of chunks) {
      merged.set(chunk, offset);
      offset += chunk.length;
    }

    const text = new TextDecoder().decode(merged);
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  } catch {
    return undefined;
  }
}

export const sentryRequestMiddleware = createMiddleware({ type: "request" }).server(
  async ({ next, request }) => {
    const { Sentry, getLogger } = await getServerDeps();
    const url = new URL(request.url);
    const logger = getLogger("middleware.sentry.request");

    const clientIPCountry = request.headers.get("EO-Client-IPCountry") ?? "unknown";

    const queryParams: Record<string, string> = {};
    url.searchParams.forEach((value, key) => {
      queryParams[key] = value;
    });

    let requestBody: unknown;
    if (request.method !== "GET" && request.method !== "HEAD") {
      const cloned = request.clone();
      requestBody = await tryReadBody(cloned.body, cloned.headers.get("content-type"));
    }

    return await Sentry.withScope(async (scope) => {
      scope.setTag("client.country", clientIPCountry);
      scope.setExtra("request.method", request.method);
      scope.setExtra("request.path", url.pathname);

      if (Object.keys(queryParams).length > 0) {
        scope.setExtra("request.query", sanitizeForSentry(queryParams));
      }
      if (requestBody !== undefined) {
        scope.setExtra("request.body", sanitizeForSentry(requestBody));
      }

      try {
        return await next();
      } catch (error) {
        if (error instanceof Response) {
          if (isRedirectResponse(error)) {
            throw error;
          }

          const responseBody = await readResponseBody(error);
          const sanitizedResponseBody = sanitizeForSentry(responseBody);
          const responseBodyDetail = toSafeDetail(sanitizedResponseBody);
          const message = buildResponseErrorMessage(error, responseBodyDetail);
          const isServerError = error.status >= 500;

          logger.error(
            {
              status: error.status,
              statusText: error.statusText || undefined,
              responseBody: sanitizedResponseBody,
              responseDetail: responseBodyDetail,
              bodyUsed: error.bodyUsed,
              contentType: error.headers.get("content-type") || undefined,
              method: request.method,
              url: safeRequestUrl(url),
            },
            "Request middleware captured response error",
          );

          if (isServerError) {
            Sentry.withScope((responseScope) => {
              responseScope.setExtra("response.status", error.status);
              if (error.statusText) {
                responseScope.setExtra("response.statusText", error.statusText);
              }
              if (sanitizedResponseBody !== undefined) {
                responseScope.setExtra("response.body", sanitizedResponseBody);
              }
              responseScope.setExtra("response.bodyUsed", error.bodyUsed);
              responseScope.setExtra("request.method", request.method);
              responseScope.setExtra("request.path", url.pathname);
              Sentry.captureException(new Error(message));
            });
          }
          throw error;
        }

        logger.error(
          {
            error,
            method: request.method,
            url: safeRequestUrl(url),
          },
          "Request middleware captured unknown error",
        );

        Sentry.withScope((errorScope) => {
          errorScope.setExtra("request.method", request.method);
          errorScope.setExtra("request.path", url.pathname);
          Sentry.captureException(error);
        });
        throw error;
      }
    });
  },
);
